/**
 * Subscription page — pre-Grand-Opening transparency view.
 *
 * Stripe checkout is wired but dormant until LLC + keys land (Phase 2).
 * Honest-date principle: rather than show a SUBSCRIBE button that fails,
 * we plainly say what's coming and when. Methodology hold #6 (Subscription
 * as the model. Privacy follows.) — no surprise, no spin.
 *
 * Behavior:
 *   - Tiers display as a "what's coming" preview (not active checkout)
 *   - Subscribe buttons disabled until Grand Opening with explicit copy
 *   - Beta-access reality stated: free through Grand Opening + 1 week
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { launchAPI } from '../lib/api';
import { Check, Crown, Users, Zap, Clock } from 'lucide-react';

const TIERS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$15',
    period: '/month',
    icon: Zap,
    features: [
      'Full interactive access',
      'AI Pattern Guide',
      '3D Globe navigation',
      'Work Center + Notepad',
      'Audio narrations',
    ],
  },
  {
    id: 'annual',
    name: 'Annual',
    price: '$120',
    period: '/year',
    icon: Crown,
    badge: 'SAVE 33%',
    features: [
      'Everything in Monthly',
      'Priority AI responses',
      'Early access to new chains',
      'Methodology certification (when issued)',
    ],
  },
  {
    id: 'household',
    name: 'Household Founder',
    price: '$500',
    period: '/lifetime',
    icon: Users,
    badge: 'FOUNDER',
    features: [
      'Everything in Annual',
      '8 household seats',
      'Direct line to the founder',
      'Custom family timelines',
      'Founder badge — never reissued',
    ],
  },
];

function formatDate(iso) {
  if (!iso) return 'Grand Opening';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return 'Grand Opening';
  }
}

export default function Subscription() {
  const { user } = useAuth();
  const [launchInfo, setLaunchInfo] = useState(null);

  useEffect(() => {
    launchAPI.status().then((r) => setLaunchInfo(r.data)).catch(() => {});
  }, []);

  const phase = launchInfo?.phase || 'countdown';
  const subsLive = phase === 'live_week' || phase === 'retired';
  const openLabel = formatDate(launchInfo?.kickstarter_open_at);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="subscription-page">
      <div className="text-center mb-10">
        <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-3">
          The full network
        </h2>
        <p className="font-mono text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
          Sources, contradictions, AI Pattern Guide, audio narration, classroom workflows — everything TSOGN does, fully unlocked.
        </p>

        {!subsLive && (
          <div
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4A84A]/30 bg-[#D4A84A]/5"
            data-testid="subs-pre-launch-banner"
          >
            <Clock size={14} className="text-[#D4A84A]" />
            <span className="font-hud text-[10px] md:text-xs tracking-widest uppercase text-[#D4A84A]">
              Subscriptions open {openLabel}
            </span>
          </div>
        )}

        <p className="font-mono text-xs md:text-sm text-white/65 mt-5 max-w-2xl mx-auto leading-relaxed">
          You're already in. Everyone who signed up before Grand Opening keeps free access through one week after launch.
          Pricing below is what kicks in after that — published now so there are no surprises.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIERS.map((tier) => {
          const Icon = tier.icon;
          const isCurrentPlan = user?.subscription_tier === tier.id && user?.subscription_active;
          return (
            <div
              key={tier.id}
              data-testid={`tier-${tier.id}`}
              className={`glass-panel p-6 relative transition-all duration-300 hover:border-[#2F8A92]/40 ${
                tier.id === 'annual' ? 'border-[#D4A84A]/40 md:scale-105' : ''
              }`}
            >
              {tier.badge && (
                <span className="absolute -top-3 right-4 font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-2 py-0.5">
                  {tier.badge}
                </span>
              )}
              <Icon className="text-[#2F8A92] mb-4" size={26} />
              <h3 className="font-hud text-[#2F8A92] text-sm md:text-base tracking-widest uppercase">{tier.name}</h3>
              <div className="mt-2 mb-4">
                <span className="font-historical text-[#D4A84A] text-3xl md:text-4xl">{tier.price}</span>
                <span className="font-mono text-xs md:text-sm text-white/55 ml-1">{tier.period}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {tier.features.map((f, i) => (
                  <li key={`${tier.id || tier.name}-feat-${i}`} className="flex items-start gap-2 font-mono text-xs md:text-sm text-white/80">
                    <Check size={13} className="text-[#2F8A92] flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {isCurrentPlan ? (
                <div className="font-hud text-xs md:text-sm tracking-wider text-[#D4A84A] text-center py-2">
                  CURRENT PLAN
                </div>
              ) : (
                <button
                  data-testid={`subscribe-${tier.id}`}
                  disabled
                  className="w-full font-hud text-xs md:text-sm tracking-wider py-2.5 transition-all duration-300 flex items-center justify-center gap-2 bg-[#52525B]/15 text-white/55 border border-[#52525B]/30 cursor-not-allowed"
                  title="Subscriptions open at Grand Opening"
                >
                  OPENS {openLabel.toUpperCase()}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center mt-8 space-y-2">
        <p className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">
          Subscription as the model. Privacy follows.
        </p>
        <p className="font-mono text-xs md:text-sm text-white/65 max-w-2xl mx-auto leading-relaxed">
          We don't sell your data. We don't bury features behind ads. The subscription pays the build.
          That's the whole arrangement.
        </p>
      </div>
    </div>
  );
}
